import React, {
    ComponentType,
    FC,
    lazy,
    PropsWithChildren,
    PropsWithoutRef,
    Suspense,
} from "react";

import { delay } from "@formant/core/common/delay";
import { logger } from "@formant/core/common/logger";
import { narrowError } from "@formant/core/common/narrowError";
import { duration } from "@formant/core/model/duration";
import { LoadingIndicator } from "@formant/ui/component/common/LoadingIndicator";

function getDisplayName(Component: ComponentType<any>) {
    return Component.displayName ?? Component.name ?? "Unknown";
}

const maxAttempts = 8;

interface ILazyOptions {
    displayName?: string;
    hideLoadingIndicator?: boolean;
}

export function Lazy<P>(
    loader: () => Promise<ComponentType<P>>,
    options: ILazyOptions | true = {},
): FC<PropsWithChildren<PropsWithoutRef<P>>> {
    const { displayName = "LazyComponent", hideLoadingIndicator = false } =
        typeof options !== "object"
            ? { hideLoadingIndicator: options }
            : options;

    const Component = lazy(async () => {
        for (let i = 1; i <= maxAttempts; i++) {
            try {
                return { default: await loader() };
            } catch (error) {
                narrowError(
                    error,
                    Error,
                    _ => _.name === "ChunkLoadError" && i !== maxAttempts,
                );
                logger.warn("Chunk load failure. Retrying", { error });
                const wait = 100 * Math.pow(2, 8) * duration.millisecond;
                const jitter = Math.random() * 100 - 50;
                await delay(wait + jitter);
            }
        }
        throw new Error("Unexpected");
    });

    (Component as ComponentType<P>).displayName = `Lazy(${displayName})`;

    const SuspendedComponent: FC<PropsWithChildren<PropsWithoutRef<P>>> = (
        props: PropsWithChildren<PropsWithoutRef<P>>,
    ) => {
        const fallback = hideLoadingIndicator ? null : (
            <LoadingIndicator visible={true} />
        );
        return (
            <Suspense fallback={fallback}>
                <Component {...props} />
            </Suspense>
        );
    };

    SuspendedComponent.displayName = `Suspend(${getDisplayName(Component)})`;

    return SuspendedComponent;
}
